import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { companyMembershipService, type SwitchCompanyResponse, type CanLeaveCompanyResponse, type TransferOwnershipResponse } from '@/services/company-membership/company-membership.service';
import { useCompanyStore } from '@/store/use-company-store';
import { useAuthStore } from '@/store/use-auth-store';
import type { CompanyMembership } from '@/types/user.types';
import { UtilsService } from '@/services/utils/utils.service';

const utils = new UtilsService();

export const useUserMemberships = (options?: { enabled?: boolean }) => {
  const { setMemberships } = useCompanyStore();
  const { isAuthenticated } = useAuthStore();
  
  return useQuery<CompanyMembership[]>({
    queryKey: ['userMemberships'],
    queryFn: async () => {
      const memberships = await companyMembershipService.getUserMemberships();
      setMemberships(memberships);
      return memberships;
    },
    enabled: options?.enabled !== false && isAuthenticated,
    retry: false,
  });
};

export const useSwitchCompany = () => {
  const queryClient = useQueryClient();
  const { setCurrentCompany, setSwitching, isSwitching } = useCompanyStore();
  const { t } = useTranslation();

  return useMutation<
    SwitchCompanyResponse,
    unknown,
    { companyId: string; suppressToast?: boolean },
    { previousCompanyId: string | null }
  >({
    retry: false,
    mutationFn: async ({ companyId }) => {
      return companyMembershipService.switchCompany(companyId);
    },
    onMutate: async ({ companyId: newCompanyId }) => {
      const storeState = useCompanyStore.getState();
      const previousCompanyId = storeState.currentCompanyId;
      
      if (storeState.isSwitching) {
        throw new Error('Company switch already in progress');
      }
      
      setSwitching(true);
      await queryClient.cancelQueries();
      setCurrentCompany(newCompanyId);

      return { previousCompanyId };
    },
    onSuccess: async (data, variables, context) => {
      const newCompanyId = data.company.id;
      setCurrentCompany(newCompanyId);
      setSwitching(false);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['company', newCompanyId] }),
        queryClient.invalidateQueries({ queryKey: ['companyMembers', newCompanyId] }),
        queryClient.invalidateQueries({ queryKey: ['invitations', newCompanyId] }),
        queryClient.invalidateQueries({ queryKey: ['subscription', 'me', newCompanyId] }),
        queryClient.invalidateQueries({ queryKey: ['companyOnboardingStatus', newCompanyId] }),
      ]);

      const { companyService } = await import('@/services/company/company.service');
      const { subscriptionService } = await import('@/services/subscription/subscription.service');
      
      queryClient.prefetchQuery({
        queryKey: ['company', newCompanyId],
        queryFn: () => companyService.getCompany(),
      });
      
      queryClient.prefetchQuery({
        queryKey: ['subscription', 'me', newCompanyId],
        queryFn: () => subscriptionService.getMySubscription(),
      });

      if (!variables.suppressToast) {
        toast.success(t('company.switchSuccess') || 'Company switched successfully');
      }
    },
    onError: (error, variables, context) => {
      const beforeState = useCompanyStore.getState();
      
      if (beforeState.isSwitching) {
        setSwitching(false);
      }
      
      if (context?.previousCompanyId !== undefined && context.previousCompanyId) {
        const currentId = useCompanyStore.getState().currentCompanyId;
        if (currentId !== context.previousCompanyId) {
          setCurrentCompany(context.previousCompanyId);
        }
      }

      if (!variables?.suppressToast) {
        const errorMessage = utils.extractErrorMessage(error, t('company.switchFailed') || 'Failed to switch company');
        toast.error(errorMessage);
      }
    },
  });
};

export const useLeaveCompany = () => {
  const queryClient = useQueryClient();
  const { currentCompanyId, refreshMemberships } = useCompanyStore();
  const { t } = useTranslation();

  return useMutation<{ success: boolean }, unknown, string>({
    mutationFn: (companyId: string) => companyMembershipService.leaveCompany(companyId),
    onSuccess: async () => {
      await refreshMemberships();
      queryClient.invalidateQueries({ queryKey: ['userMemberships'] });
      queryClient.invalidateQueries({ queryKey: ['company'] });
      queryClient.invalidateQueries({ queryKey: ['companyMembers'] });

      toast.success(t('company.leftSuccess') || 'Left company successfully');
    },
    onError: (error: unknown) => {
      const errorMessage = utils.extractErrorMessage(error, t('company.leftFailed') || 'Failed to leave company');
      toast.error(errorMessage);
    },
  });
};

export const useTransferOwnership = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<TransferOwnershipResponse, unknown, { companyId: string; newOwnerId: string }>({
    mutationFn: ({ companyId, newOwnerId }) =>
      companyMembershipService.transferOwnership(companyId, newOwnerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company'] });
      queryClient.invalidateQueries({ queryKey: ['companyMembers'] });
      queryClient.invalidateQueries({ queryKey: ['userMemberships'] });

      toast.success(t('company.ownershipTransferred') || 'Ownership transferred successfully');
    },
    onError: (error: unknown) => {
      const errorMessage = utils.extractErrorMessage(error, t('company.transferFailed') || 'Failed to transfer ownership');
      toast.error(errorMessage);
    },
  });
};

export const useCanLeaveCompany = (companyId: string) => {
  return useQuery<CanLeaveCompanyResponse>({
    queryKey: ['canLeaveCompany', companyId],
    queryFn: () => companyMembershipService.canLeaveCompany(companyId),
    enabled: !!companyId,
  });
};

